// Notification Sound Utility
// Uses Web Audio API to generate pleasant notification sounds

class NotificationSound {
  constructor() {
    this.audioContext = null;
    this.enabled = true;
    this.volume = 0.3; // Default volume (0-1)
    
    // Load user preference from localStorage
    const savedEnabled = localStorage.getItem('notificationSoundEnabled');
    if (savedEnabled !== null) {
      this.enabled = savedEnabled === 'true';
    }
    
    const savedVolume = localStorage.getItem('notificationSoundVolume');
    if (savedVolume !== null) {
      this.volume = parseFloat(savedVolume);
    }
  }
  
  // Initialize audio context (must be called after user interaction)
  init() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    return this.audioContext;
  }
  
  // Enable/disable sounds
  setEnabled(enabled) {
    this.enabled = enabled;
    localStorage.setItem('notificationSoundEnabled', enabled.toString());
  }
  
  // Set volume (0-1)
  setVolume(volume) {
    this.volume = Math.max(0, Math.min(1, volume));
    localStorage.setItem('notificationSoundVolume', this.volume.toString());
  }
  
  // Play a pleasant chime sound (for success/info notifications)
  playChime() {
    if (!this.enabled) return;
    
    try {
      const ctx = this.init();
      if (ctx.state === 'suspended') {
        ctx.resume();
      }
      
      const now = ctx.currentTime;
      
      // Create oscillator for the main tone
      const osc1 = ctx.createOscillator();
      const gain1 = ctx.createGain();
      
      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(880, now); // A5
      osc1.frequency.exponentialRampToValueAtTime(1320, now + 0.1); // E6
      
      gain1.gain.setValueAtTime(this.volume * 0.4, now);
      gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.3);
      
      osc1.connect(gain1);
      gain1.connect(ctx.destination);
      
      osc1.start(now);
      osc1.stop(now + 0.3);
      
      // Second harmonic for richness
      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      
      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(1320, now + 0.05); // E6
      osc2.frequency.exponentialRampToValueAtTime(1760, now + 0.15); // A6
      
      gain2.gain.setValueAtTime(this.volume * 0.2, now + 0.05);
      gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.35);
      
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      
      osc2.start(now + 0.05);
      osc2.stop(now + 0.35);
      
    } catch (e) {
      console.warn('Could not play notification sound:', e);
    }
  }
  
  // Play a subtle pop sound (for messages/alerts)
  playPop() {
    if (!this.enabled) return;
    
    try {
      const ctx = this.init();
      if (ctx.state === 'suspended') {
        ctx.resume();
      }
      
      const now = ctx.currentTime;
      
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.type = 'sine';
      osc.frequency.setValueAtTime(600, now);
      osc.frequency.exponentialRampToValueAtTime(200, now + 0.1);
      
      gain.gain.setValueAtTime(this.volume * 0.5, now);
      gain.gain.exponentialRampToValueAtTime(0.01, now + 0.15);
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.start(now);
      osc.stop(now + 0.15);
      
    } catch (e) {
      console.warn('Could not play notification sound:', e);
    }
  }
  
  // Play an error/warning sound
  playError() {
    if (!this.enabled) return;
    
    try {
      const ctx = this.init();
      if (ctx.state === 'suspended') {
        ctx.resume();
      }
      
      const now = ctx.currentTime;
      
      // Two-tone descending for error
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(400, now);
      osc.frequency.setValueAtTime(300, now + 0.1);
      
      gain.gain.setValueAtTime(this.volume * 0.4, now);
      gain.gain.exponentialRampToValueAtTime(0.01, now + 0.25);
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.start(now);
      osc.stop(now + 0.25);
      
    } catch (e) {
      console.warn('Could not play notification sound:', e);
    }
  }
  
  // Play a success sound (triumphant)
  playSuccess() {
    if (!this.enabled) return;
    
    try {
      const ctx = this.init();
      if (ctx.state === 'suspended') {
        ctx.resume();
      }
      
      const now = ctx.currentTime;
      
      // Ascending arpeggio
      const notes = [523.25, 659.25, 783.99]; // C5, E5, G5
      
      notes.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, now + i * 0.08);
        
        gain.gain.setValueAtTime(0, now + i * 0.08);
        gain.gain.linearRampToValueAtTime(this.volume * 0.3, now + i * 0.08 + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.01, now + i * 0.08 + 0.3);
        
        osc.connect(gain);
        gain.connect(ctx.destination);
        
        osc.start(now + i * 0.08);
        osc.stop(now + i * 0.08 + 0.3);
      });
      
    } catch (e) {
      console.warn('Could not play notification sound:', e);
    }
  }
  
  // Play a message received sound
  playMessage() {
    if (!this.enabled) return;
    
    try {
      const ctx = this.init();
      if (ctx.state === 'suspended') {
        ctx.resume();
      }
      
      const now = ctx.currentTime;
      
      // Soft double-chime
      [0, 0.12].forEach((delay, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        
        osc.type = 'sine';
        osc.frequency.setValueAtTime(i === 0 ? 800 : 1000, now + delay);
        
        gain.gain.setValueAtTime(this.volume * 0.25, now + delay);
        gain.gain.exponentialRampToValueAtTime(0.01, now + delay + 0.2);
        
        osc.connect(gain);
        gain.connect(ctx.destination);
        
        osc.start(now + delay);
        osc.stop(now + delay + 0.2);
      });
      
    } catch (e) {
      console.warn('Could not play notification sound:', e);
    }
  }
}

// Singleton instance
const notificationSound = new NotificationSound();

export default notificationSound;
